import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listScanAnalysisQueue, type ScanAnalysisQueueItem } from '../lib/api';
import { ScanAnalysisSettingsPanel } from '../components/ScanAnalysisSettingsPanel';
import { ProposedAppChangeResolutionControl } from '../components/ProposedAppChangeResolutionControl';
import { theme } from '../theme';

export function ScanAnalysisQueuePage() {
  const [items, setItems] = useState<ScanAnalysisQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedScanId, setExpandedScanId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    void refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await listScanAnalysisQueue({ limit: 60 });
      setItems(
        res.items.filter(
          (i) => i.review.status === 'pending-review' || i.review.status === 'analysis-failed',
        ),
      );
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h1 style={{ marginRight: 'auto' }}>Scan Analysis Review</h1>
        <button
          onClick={() => setSettingsOpen((s) => !s)}
          style={{
            padding: '6px 12px',
            background: settingsOpen ? theme.surface.header : 'transparent',
            color: theme.text.secondary,
            border: `1px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.md,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {settingsOpen ? '▾ Settings' : '▸ Settings'}
        </button>
      </div>
      <p style={{ color: theme.text.secondary, fontSize: 13 }}>
        AI-generated diagnoses + proposed OBD commands for scans flagged as incomplete. Approval
        happens via the <code>scripts/review-pending-analyses.mjs</code> CLI helper (Claude reviews
        each proposal with deeper research than a glance can give). Use this page to read the
        diagnoses, stamp per-proposal resolution notes, and follow the PDF link to see the full
        report.
      </p>
      {settingsOpen && (
        <div
          style={{
            marginBottom: 24,
            padding: '16px 18px',
            border: `1px solid ${theme.surface.borderStrong}`,
            borderRadius: theme.radius.lg,
            background: theme.surface.panel,
          }}
        >
          <h2 style={{ margin: 0, marginBottom: 8, fontSize: 16 }}>Settings</h2>
          <ScanAnalysisSettingsPanel />
        </div>
      )}
      {error && <p style={{ color: theme.status.error }}>Error: {error}</p>}
      {loading && <p>Loading…</p>}
      {!loading && items.length === 0 && (
        <p style={{ color: theme.status.success }}>No analyses awaiting review — inbox zero.</p>
      )}
      {!loading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map((item) => {
            const expanded = expandedScanId === item.scanId;
            const vehicleLabel =
              item.make && item.model
                ? `${item.modelYear ?? ''} ${item.make} ${item.model}`.trim()
                : item.vin || '(unknown vehicle)';
            const isFailed = item.review.status === 'analysis-failed';
            return (
              <div
                key={item.scanId}
                style={{
                  padding: '14px 16px',
                  border: `1px solid ${isFailed ? theme.status.errorBorder : theme.surface.border}`,
                  borderRadius: theme.radius.lg,
                  background: theme.surface.panel,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto auto',
                    gap: 16,
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {vehicleLabel}
                      {item.scanShortcodeBody && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontFamily: theme.fonts.mono,
                            fontSize: 12,
                            color: theme.text.tertiary,
                            fontWeight: 400,
                          }}
                        >
                          OB-{item.scanShortcodeBody}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: theme.text.secondary }}>
                      Scanned {new Date(item.scannedAt).toLocaleString()} · trigger:{' '}
                      {item.review.triggerReason}
                      {item.signalSetStats && (
                        <>
                          {' · '}
                          {item.signalSetStats.readingsCount} / {item.signalSetStats.totalCommands}{' '}
                          readings
                        </>
                      )}
                    </div>
                    {isFailed && (
                      <div style={{ fontSize: 12, color: theme.status.error, marginTop: 4 }}>
                        Analysis failed: {item.review.analysisError || '(no error captured)'}
                      </div>
                    )}
                  </div>
                  {item.reportUrl && (
                    <a
                      href={item.reportUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 13 }}
                    >
                      View PDF
                    </a>
                  )}
                  {item.batteryReportUrl && (
                    <a
                      href={item.batteryReportUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 13 }}
                    >
                      Battery PDF
                    </a>
                  )}
                  <Link to={`/scans/${encodeURIComponent(item.scanId)}`} style={{ fontSize: 13 }}>
                    Scan detail
                  </Link>
                  <button
                    onClick={() => setExpandedScanId(expanded ? null : item.scanId)}
                    style={{
                      padding: '8px 14px',
                      background: 'transparent',
                      color: theme.text.primary,
                      border: `1px solid ${theme.surface.borderStrong}`,
                      borderRadius: theme.radius.md,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    {expanded ? 'Collapse' : 'Review'}
                  </button>
                </div>

                {expanded && (
                  <div
                    style={{
                      marginTop: 14,
                      paddingTop: 14,
                      borderTop: `1px solid ${theme.surface.border}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                        Diagnosis
                      </div>
                      <div
                        style={{ fontSize: 13, color: theme.text.primary, whiteSpace: 'pre-wrap' }}
                      >
                        {item.review.diagnosis || '(no diagnosis — analysis failed)'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                        Proposed commands ({item.review.proposedCommands.length})
                      </div>
                      {item.review.proposedCommands.length === 0 ? (
                        <div style={{ fontSize: 13, color: theme.text.tertiary }}>
                          (model proposed none)
                        </div>
                      ) : (
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: 18,
                            fontSize: 13,
                            color: theme.text.primary,
                          }}
                        >
                          {item.review.proposedCommands.map((c, idx) => (
                            <li key={idx} style={{ marginBottom: 6 }}>
                              <code style={{ fontFamily: theme.fonts.mono }}>
                                {c.header ? `${c.header} ` : ''}
                                {c.mode} {c.pid}
                              </code>
                              {c.label ? ` — ${c.label}` : ''}
                              {c.rationale && (
                                <div style={{ color: theme.text.secondary, fontSize: 12 }}>
                                  {c.rationale}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {item.review.proposedAppChanges &&
                      item.review.proposedAppChanges.length > 0 && (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                            Proposed app-level changes ({item.review.proposedAppChanges.length})
                          </div>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: 18,
                              fontSize: 13,
                              color: theme.text.primary,
                            }}
                          >
                            {item.review.proposedAppChanges.map((change, idx) => (
                              <li key={idx} style={{ marginBottom: 8 }}>
                                <code
                                  style={{
                                    fontFamily: theme.fonts.mono,
                                    fontSize: 11,
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    background: theme.surface.header,
                                    marginRight: 6,
                                  }}
                                >
                                  {change.category}
                                </code>
                                <strong>{change.summary}</strong>
                                <span
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 11,
                                    color:
                                      change.priority === 'high'
                                        ? theme.status.error
                                        : change.priority === 'medium'
                                          ? theme.status.warning
                                          : theme.text.tertiary,
                                  }}
                                >
                                  ({change.priority})
                                </span>
                                <div
                                  style={{
                                    color: theme.text.secondary,
                                    fontSize: 12,
                                    marginTop: 2,
                                  }}
                                >
                                  {change.details}
                                </div>
                                <ProposedAppChangeResolutionControl
                                  scanId={item.scanId}
                                  index={idx}
                                  change={change}
                                  onResolved={(updated) =>
                                    setItems((prev) =>
                                      prev.map((p) => (p.scanId === updated.scanId ? updated : p)),
                                    )
                                  }
                                />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    {/* Per-PR-120: Approve / Reject controls removed from the
                        dashboard. Claude (CLI) reviews proposals via the
                        scripts/review-pending-analyses.mjs helper now —
                        commands that pass review land in the Vehicle KB
                        through the same PATCH endpoint, just with
                        reviewedVia='cli' attribution. ProposedAppChangeResolutionControl
                        below still works for staff to stamp per-proposal
                        resolution (kept because it's per-proposal-grain
                        feedback, not a release-it-to-the-fleet decision). */}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
